import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Analytics } from '@vercel/analytics/react';

// Routes wrapper that reports route changes as performance transactions to Sentry.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/signUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Lesson from './pages/Lesson';
import Profile from './pages/Profile';
import Activities from './pages/Activities';
import Reading from './pages/Reading';
import Writing from './pages/Writing';
import Listening from './pages/Listening';
import Speaking from './pages/Speaking';
import Vocabulary from './pages/Vocabulary';
import Grammar from './pages/Grammar';
import Settings from './pages/Settings';
import AdminUsers from './pages/AdminUsers';
import AdminFeedback from './pages/AdminFeedback';
import TermsAndConditions from './pages/TermsAndConditions';
import Feedback from './pages/Feedback';
import Pricing from './pages/Pricing';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import NavBar from './components/navbar';
import Footer from './components/Footer';
import AuthRedirect from './components/AuthRedirect';
import ChatWidget from './components/ChatWidget';
import PageMotion from './components/PageMotion';

function RouteLoading() {
  return (
    <div className="page-loading" role="status" aria-live="polite">
      <span className="page-loading-spinner" aria-hidden />
    </div>
  );
}

export function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  return user ? children : <Navigate to="/login" replace />;
}

export function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

export function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1">
        <PageMotion>{children}</PageMotion>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

export function AppRoutes() {
  return (
    <AuthRedirect>
      <Layout>
        <SentryRoutes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/reading" element={<PrivateRoute><Reading /></PrivateRoute>} />
          <Route path="/writing" element={<PrivateRoute><Writing /></PrivateRoute>} />
          <Route path="/listening" element={<PrivateRoute><Listening /></PrivateRoute>} />
          <Route path="/speaking" element={<PrivateRoute><Speaking /></PrivateRoute>} />
          <Route path="/vocabulary" element={<PrivateRoute><Vocabulary /></PrivateRoute>} />
          <Route path="/grammar" element={<PrivateRoute><Grammar /></PrivateRoute>} />
          <Route path="/activities" element={<PrivateRoute><Activities /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
          <Route path="/lesson/:id" element={<PrivateRoute><Lesson /></PrivateRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/feedback" element={<AdminRoute><AdminFeedback /></AdminRoute>} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/feedback" element={<Feedback />} />
          {/* Direct URL only — not linked from nav/footer until Pro features ship. */}
          <Route path="/pricing" element={<PrivateRoute><Pricing /></PrivateRoute>} />
          <Route path="/billing/success" element={<PrivateRoute><BillingSuccess /></PrivateRoute>} />
          <Route path="/billing/cancel" element={<PrivateRoute><BillingCancel /></PrivateRoute>} />
        </SentryRoutes>
        <Analytics />
      </Layout>
    </AuthRedirect>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </SubscriptionProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
